"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  type SharedFile,
  type SharedFileActivitySortBy,
  type SharedFilesSortBy,
  type TransferProgress,
  useDeleteSharedFiles,
  useDownloadSharedFiles,
  useFileShareConfig,
  useSharedFileActivity,
  useSharedFiles,
  useUploadSharedFiles,
} from "@/hooks/use-file-share";
import { toast } from "@/lib/instrumented-toast";
import { cn, formatDate, formatDateTime, formatFileSize, formatRelativeTime } from "@/lib/utils";
import { useOrgStore, useUserStore } from "@/stores";
import { AxiosError } from "axios";
import { ChevronDown, ChevronUp, Download, Trash2, Upload, X } from "lucide-react";
import React from "react";

import { Pager } from "./pager";
import { SortableHead, type SortState, toggleSort } from "./sortable-head";

const PAGE_SIZE = 20;

const fileKey = (file: File) => `${file.name}\u0000${file.size}\u0000${file.lastModified}`;

function isServiceUnavailable(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 503;
}

function overallPercent(progress: TransferProgress | null): number {
  if (!progress) return 0;
  return Math.round(((progress.index + progress.percent / 100) / progress.total) * 100);
}

function MessageCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <Card className="w-96">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

function DateCell({ value, showTime }: { value: string; showTime: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">
          {showTime ? formatDateTime(value, { includeRelative: false }) : formatDate(value)}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{formatRelativeTime(value)}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function ActivityTable({ orgId }: { orgId: string }) {
  const [sort, setSort] = React.useState<SortState<SharedFileActivitySortBy>>({
    column: "created_at",
    direction: "desc",
  });
  const [page, setPage] = React.useState(1);
  const activityQuery = useSharedFileActivity(orgId, {
    page,
    pageSize: PAGE_SIZE,
    sortBy: sort.column,
    sortDir: sort.direction,
  });
  const events = activityQuery.data?.events ?? [];
  const total = activityQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSort = (column: SharedFileActivitySortBy) => {
    setSort((prev) => toggleSort(prev, column));
    setPage(1);
  };

  if (activityQuery.isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (activityQuery.isError) {
    return <p className="text-center text-muted-foreground">Activity could not be loaded. Try again later.</p>;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead column="created_at" label="When" sort={sort} onSort={handleSort} />
            <SortableHead column="user_email" label="User" sort={sort} onSort={handleSort} />
            <SortableHead column="action" label="Action" sort={sort} onSort={handleSort} />
            <SortableHead column="filename" label="File" sort={sort} onSort={handleSort} />
            <SortableHead column="size_bytes" label="Size" sort={sort} onSort={handleSort} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                No activity yet.
              </TableCell>
            </TableRow>
          ) : (
            events.map((event) => (
              <TableRow key={event.id}>
                <TableCell>{formatDateTime(event.created_at, { includeRelative: false })}</TableCell>
                <TableCell>{event.user_email}</TableCell>
                <TableCell className="capitalize">{event.action}</TableCell>
                <TableCell className="font-medium">{event.filename}</TableCell>
                <TableCell>{formatFileSize(event.size_bytes ?? undefined)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {total > 0 && (
        <Pager
          page={page}
          pageCount={pageCount}
          onPage={setPage}
          label="activity"
          summary={`Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} events`}
        />
      )}
    </>
  );
}

export function FileShareView() {
  const user = useUserStore.use.user();
  const selectedOrgId = useOrgStore.use.selectedOrgId();

  // The persisted selectedOrgId may point at an org the token no longer grants, which would
  // 403 every request; only query orgs that appear in the token permissions.
  const accessibleOrgIds = user?.permissions?.map((p) => p.id) ?? [];
  const orgId = user?.superAdmin
    ? selectedOrgId || undefined
    : selectedOrgId && accessibleOrgIds.includes(selectedOrgId)
      ? selectedOrgId
      : (accessibleOrgIds[0] ?? user?.defaultOrg?.id);
  const hasOrgAccess = user?.superAdmin || (user?.organizations && user.organizations.length > 0);

  const [sort, setSort] = React.useState<SortState<SharedFilesSortBy>>({ column: "uploaded_at", direction: "desc" });
  const [view, setView] = React.useState<"files" | "activity">("files");
  const [page, setPage] = React.useState(1);
  const configQuery = useFileShareConfig();
  const filesQuery = useSharedFiles(orgId, {
    page,
    pageSize: PAGE_SIZE,
    sortBy: sort.column,
    sortDir: sort.direction,
  });
  const uploadMutation = useUploadSharedFiles();
  const downloadMutation = useDownloadSharedFiles();
  const deleteMutation = useDeleteSharedFiles();

  const [files, setFiles] = React.useState<File[]>([]);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<TransferProgress | null>(null);
  const [downloadProgress, setDownloadProgress] = React.useState<TransferProgress | null>(null);
  const [inputKey, setInputKey] = React.useState(0);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(new Set());
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const fileInputId = React.useId();
  const acknowledgeId = React.useId();

  if (!hasOrgAccess) {
    return <MessageCard title="Access Denied" description="You do not have permission to view shared files." />;
  }

  if (!orgId) {
    return <MessageCard title="File Share" description="Select an organization to view shared files." />;
  }

  if (configQuery.data?.enabled === false || isServiceUnavailable(filesQuery.error)) {
    return <MessageCard title="File Share" description="File Share is not available in this environment." />;
  }

  const config = configQuery.data;
  const maxUploadBytes = config?.max_upload_bytes;
  const sharedFiles = filesQuery.data?.files ?? [];
  const total = filesQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const canUpload = filesQuery.data?.can_upload ?? false;
  const canViewActivity = filesQuery.data?.can_view_activity ?? false;
  const canSubmit = configQuery.isSuccess && files.length > 0 && acknowledged && !uploadMutation.isPending;
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const percent = overallPercent(uploadProgress);
  const selectedFiles = sharedFiles.filter((file) => selectedIds.has(file.id));
  const allSelected = sharedFiles.length > 0 && selectedFiles.length === sharedFiles.length;
  const headerChecked = allSelected ? true : selectedFiles.length > 0 ? "indeterminate" : false;
  const canDeleteSome = sharedFiles.some((file) => file.can_delete);
  const canDeleteSelection = selectedFiles.length > 0 && selectedFiles.every((file) => file.can_delete);

  const clearSelection = () => {
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
  };

  const goToPage = (next: number) => {
    setPage(next);
    clearSelection();
  };

  const resetUploadForm = () => {
    setFiles([]);
    setAcknowledged(false);
    setInputKey((key) => key + 1);
    goToPage(1);
  };

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    const tooLarge = maxUploadBytes === undefined ? [] : selected.filter((file) => file.size > maxUploadBytes);
    if (tooLarge.length > 0 && maxUploadBytes !== undefined) {
      const subject =
        tooLarge.length === 1 ? `${tooLarge[0].name} is too large` : `${tooLarge.length} files are too large`;
      toast.error(`${subject}. Maximum size is ${formatFileSize(maxUploadBytes)}.`);
    }
    const accepted = selected.filter((file) => !tooLarge.includes(file));
    setFiles((prev) => {
      const known = new Set(prev.map(fileKey));
      return [...prev, ...accepted.filter((file) => !known.has(fileKey(file)))];
    });
    setInputKey((key) => key + 1); // reset the native input so the same files can be picked again
  };

  const removeFile = (target: File) => {
    setFiles((prev) => prev.filter((file) => file !== target));
    setInputKey((key) => key + 1); // reset the native input so the same file can be chosen again
  };

  const handleUpload = () => {
    if (files.length === 0) return;
    uploadMutation.mutate(
      {
        orgId,
        files,
        onProgress: setUploadProgress,
        onFileUploaded: (done) => setFiles((prev) => prev.filter((file) => file !== done)),
      },
      { onSuccess: resetUploadForm, onSettled: () => setUploadProgress(null) },
    );
  };

  const handleSort = (column: SharedFilesSortBy) => {
    setSort((prev) => toggleSort(prev, column));
    goToPage(1);
  };

  const toggleSelected = (fileId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(fileId)) next.add(fileId);
      return next;
    });

  const handleDownload = (targets: SharedFile[]) =>
    downloadMutation.mutate(
      {
        orgId,
        files: targets.map(({ id, filename, size_bytes }) => ({ id, filename, size_bytes })),
        onProgress: setDownloadProgress,
      },
      { onSuccess: clearSelection, onSettled: () => setDownloadProgress(null) },
    );

  const downloadSelectedLabel =
    downloadMutation.isPending && downloadProgress
      ? `Preparing archive ${downloadProgress.index + 1} of ${downloadProgress.total}...`
      : `Download selected (${selectedFiles.length})`;

  const handleDelete = (targets: SharedFile[]) =>
    deleteMutation.mutate(
      { orgId, files: targets },
      {
        onSuccess: () => {
          const gone = new Set(targets.map((file) => file.id));
          setSelectedIds((prev) => new Set([...prev].filter((id) => !gone.has(id))));
          if (sharedFiles.length === targets.length && page > 1) setPage(page - 1);
        },
        onSettled: () => {
          setConfirmDeleteId(null);
          setConfirmBulkDelete(false);
        },
      },
    );

  const selectionSummary =
    files.length === 0
      ? "No files chosen"
      : `${files.length} ${files.length === 1 ? "file" : "files"} (${formatFileSize(totalBytes)})`;
  const uploadLabel = uploadMutation.isPending
    ? uploadProgress && uploadProgress.total > 1
      ? `Uploading ${uploadProgress.index + 1} of ${uploadProgress.total}... ${percent}%`
      : `Uploading... ${percent}%`
    : files.length > 1
      ? `Upload ${files.length} files`
      : "Upload";

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-3xl font-bold">File Share</h1>
        <p className="text-muted-foreground">
          {config
            ? `Files shared with your organization are removed ${config.retention_days} days after upload.`
            : "Files shared with your organization."}
        </p>
      </div>

      {canUpload && (
        <Collapsible open={uploadOpen} onOpenChange={setUploadOpen} asChild>
          <Card>
            <CardHeader>
              <CardTitle>Upload files</CardTitle>
              {maxUploadBytes !== undefined && (
                <CardDescription>Maximum size per file is {formatFileSize(maxUploadBytes)}.</CardDescription>
              )}
              <CardAction>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label={uploadOpen ? "Hide upload panel" : "Show upload panel"}>
                    {uploadOpen ? "Hide" : "Show"}
                    {uploadOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
              </CardAction>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    key={inputKey}
                    id={fileInputId}
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={handleFilesChange}
                    disabled={uploadMutation.isPending}
                  />
                  <Label htmlFor={fileInputId} className={cn(buttonVariants({ variant: "outline" }), "cursor-pointer")}>
                    <Upload className="h-4 w-4" />
                    Choose files
                  </Label>
                  <span className="text-sm text-muted-foreground">{selectionSummary}</span>
                </div>
                {files.length > 0 && (
                  <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
                    {files.map((file, index) => (
                      <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2">
                        <span>
                          {file.name} ({formatFileSize(file.size)})
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => removeFile(file)}
                          disabled={uploadMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-start gap-2">
                  <Checkbox
                    id={acknowledgeId}
                    checked={acknowledged}
                    onCheckedChange={(checked) => setAcknowledged(checked === true)}
                    disabled={uploadMutation.isPending}
                  />
                  <Label htmlFor={acknowledgeId} className="text-sm leading-snug">
                    I confirm these files do not contain export-controlled, CUI, or otherwise classified data.
                  </Label>
                </div>
                {uploadMutation.isPending && (
                  <div
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  >
                    <div className="h-full bg-primary transition-[width]" style={{ width: `${percent}%` }} />
                  </div>
                )}
                <div>
                  <Button onClick={handleUpload} disabled={!canSubmit}>
                    {uploadLabel}
                  </Button>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      <Tabs value={view} onValueChange={(next) => setView(next === "activity" ? "activity" : "files")}>
        <Card>
          <CardHeader>
            <CardTitle>Shared files</CardTitle>
            {canViewActivity && (
              <TabsList>
                <TabsTrigger value="files">Files</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>
            )}
            {view === "files" && sharedFiles.length > 0 && (
              <CardAction className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(selectedFiles)}
                  disabled={selectedFiles.length === 0 || downloadMutation.isPending}
                >
                  <Download className="h-4 w-4" />
                  {downloadSelectedLabel}
                </Button>
                {canDeleteSome &&
                  (confirmBulkDelete ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">
                        Delete {selectedFiles.length} {selectedFiles.length === 1 ? "file" : "files"}?
                      </span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(selectedFiles)}
                        disabled={!canDeleteSelection || deleteMutation.isPending}
                      >
                        Yes
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmBulkDelete(false)}>
                        No
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setConfirmBulkDelete(true)}
                      disabled={!canDeleteSelection || deleteMutation.isPending}
                      title={
                        selectedFiles.length > 0 && !canDeleteSelection
                          ? "Only files you uploaded can be deleted"
                          : undefined
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete selected ({selectedFiles.length})
                    </Button>
                  ))}
              </CardAction>
            )}
          </CardHeader>
          <CardContent>
            <TabsContent value="files">
              {filesQuery.isLoading && (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              )}
              {filesQuery.isError && (
                <p className="text-center text-muted-foreground">Shared files could not be loaded. Try again later.</p>
              )}
              {filesQuery.isSuccess && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          aria-label="Select all files on this page"
                          checked={headerChecked}
                          disabled={sharedFiles.length === 0}
                          onCheckedChange={(checked) =>
                            setSelectedIds(checked === true ? new Set(sharedFiles.map((file) => file.id)) : new Set())
                          }
                        />
                      </TableHead>
                      <SortableHead column="filename" label="Name" sort={sort} onSort={handleSort} />
                      <SortableHead column="size_bytes" label="Size" sort={sort} onSort={handleSort} />
                      <TableHead>Uploaded by</TableHead>
                      <SortableHead column="uploaded_at" label="Uploaded" sort={sort} onSort={handleSort} />
                      <SortableHead column="expires_at" label="Expires" sort={sort} onSort={handleSort} />
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sharedFiles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          {canUpload
                            ? "No files shared yet."
                            : "Istari has not shared any files with your organization yet."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      sharedFiles.map((sharedFile) => (
                        <TableRow
                          key={sharedFile.id}
                          data-state={selectedIds.has(sharedFile.id) ? "selected" : undefined}
                        >
                          <TableCell>
                            <Checkbox
                              aria-label={`Select ${sharedFile.filename}`}
                              checked={selectedIds.has(sharedFile.id)}
                              onCheckedChange={() => toggleSelected(sharedFile.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{sharedFile.filename}</TableCell>
                          <TableCell>{formatFileSize(sharedFile.size_bytes)}</TableCell>
                          <TableCell>{sharedFile.uploaded_by}</TableCell>
                          <TableCell>
                            <DateCell value={sharedFile.uploaded_at} showTime />
                          </TableCell>
                          <TableCell>
                            <DateCell value={sharedFile.expires_at} showTime={false} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                title="Download file"
                                onClick={() => handleDownload([sharedFile])}
                                disabled={downloadMutation.isPending}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {sharedFile.can_delete &&
                                (confirmDeleteId === sharedFile.id ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground">Delete?</span>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleDelete([sharedFile])}
                                      disabled={deleteMutation.isPending}
                                    >
                                      Yes
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(null)}>
                                      No
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    title="Delete file"
                                    onClick={() => setConfirmDeleteId(sharedFile.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
              {filesQuery.isSuccess && total > 0 && (
                <Pager
                  page={page}
                  pageCount={pageCount}
                  onPage={goToPage}
                  label="files"
                  summary={`Showing ${rangeStart}–${rangeEnd} of ${total} files`}
                />
              )}
            </TabsContent>
            {canViewActivity && (
              <TabsContent value="activity">
                <ActivityTable orgId={orgId} />
              </TabsContent>
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
